/* eslint-disable no-console */
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { isEqual } from 'lodash';
import styled from 'styled-components';
import ConfigContext from '../../contexts/ConfigContext';
import { AgGrid } from '../AgGrid';
import { Loader } from '../Loader';
import { TableAgGridWrapper } from './components/TableAgGridWrapper';
import useCancellableAnimationFrame from './hooks/useCancellableAnimationFrame';
import { useCloseColumnsPanelOnOutsideClick } from './hooks/useCloseColumnsPanelOnOutsideClick';
import { useColumnPanelLocate } from './hooks/useColumnPanelLocate';
import { usePersistedColumnState } from './hooks/usePersistedColumnState';
import { actionColumnStaticOptions, GRID_STATIC_OPTIONS } from './constants';
import { updateGrouping as updateGroupingUtil } from './utils/updateGrouping';
import { isGridApiValid } from './utils/isGridApiValid';

export const LoaderWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.black}0D;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  z-index: 10;
`;

const LoaderText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
`;

export const TableAgGrid = ({
  rowData = [],
  summaryRow = null,
  columnDefs = [],
  defaultColDef = {},
  autoGroupColumnDef = {},
  sideBar = {},
  gridOptions = {},
  groupBy,
  defaultSortColumn,
  clickableGroupRowClasses = [],
  changeGroupByCallback,
  persistenceKey = 'agGridColumnState',
  actionRenderer,
  actionRendererParams,
  actionColumnOptions = {},
  noRowsOverlayComponentParams = {},
  isLoading,
  isLoadingExtraData,
  getRowId,
  defaultExtendsRows = {}
}) => {
  const [api, setApi] = useState();
  const gridWrapperRef = useRef(null);
  const { language, t, isRtl } = useContext(ConfigContext);
  const lastGroupByRef = useRef(groupBy);
  const lastAppliedDefaultExtendsKeyRef = useRef(null);
  const { requestNextFrame, cancelNextFrame } = useCancellableAnimationFrame();

  const finalColumnDefs = useMemo(() => {
    if (!actionRenderer) return columnDefs;

    return [
      ...columnDefs,
      {
        ...actionColumnStaticOptions(isRtl),
        cellRenderer: actionRenderer,
        cellRendererParams: actionRendererParams,
        ...actionColumnOptions
      }
    ];
  }, [columnDefs, actionRenderer, actionRendererParams, actionColumnOptions, isRtl]);
  const lastAppliedColumnDefsRef = useRef(finalColumnDefs);

  const { loadColumnState, saveColumnState } = usePersistedColumnState({ storageKey: persistenceKey });
  useCloseColumnsPanelOnOutsideClick(api, gridWrapperRef);
  useColumnPanelLocate(api, gridWrapperRef, { t, onColumnRevealed: saveColumnState });

  const updateGrouping = useCallback(updateGroupingUtil, []);

  const applyDefaultExtendsRowsIfNeeded = useCallback(
    (gridApi) => {
      if (!isGridApiValid(gridApi)) return;
      if (!defaultExtendsRows || Object.keys(defaultExtendsRows).length === 0) return;

      const key = `${String(groupBy)}#${JSON.stringify(defaultExtendsRows)}`;
      if (lastAppliedDefaultExtendsKeyRef.current === key) return;

      try {
        // Apply by group index (0-based) for top-level groups only.
        const topLevelGroupNodes = [];
        gridApi.forEachNode((node) => {
          if (node.group && node.level === 0) {
            topLevelGroupNodes.push(node);
          }
        });

        topLevelGroupNodes.forEach((node, index) => {
          if (defaultExtendsRows[index] !== undefined) {
            node.setExpanded(!!defaultExtendsRows[index]);
          }
        });

        // Mark as handled so we don't override user-expanded state repeatedly.
        lastAppliedDefaultExtendsKeyRef.current = key;
      } catch (error) {
        console.warn('TableAgGrid: Error applying default expanded rows', error);
      }
    },
    [defaultExtendsRows, groupBy]
  );

  useEffect(() => {
    if (!isGridApiValid(api)) return;

    try {
      api.setGridOption('rowData', rowData);
      api.refreshCells({ force: true });
      api.setGridOption('pinnedBottomRowData', summaryRow && rowData?.length > 0 ? [summaryRow] : []);
    } catch (error) {
      console.warn('TableAgGrid: Error updating row data', error);
    }
  }, [api, rowData, summaryRow]);

  useEffect(() => {
    if (!isGridApiValid(api) || finalColumnDefs.length === 0) return;

    const hasColDiff = !isEqual(lastAppliedColumnDefsRef.current, finalColumnDefs);
    const hasGroupByChange = lastGroupByRef.current !== groupBy;

    if (hasGroupByChange) {
      lastGroupByRef.current = groupBy;
      lastAppliedDefaultExtendsKeyRef.current = null;
    }

    try {
      if (hasColDiff && hasGroupByChange) {
        // Both column and groupBy change (update grouping first then columns to prevent visual glitch)
        updateGrouping(api, groupBy);
        lastAppliedColumnDefsRef.current = finalColumnDefs;
        api.setGridOption('columnDefs', finalColumnDefs);
        api.refreshToolPanel?.();

        requestNextFrame(() => {
          if (!isGridApiValid(api)) return;
          try {
            updateGrouping(api, groupBy);
            applyDefaultExtendsRowsIfNeeded(api);
            api.refreshCells({ force: true });
          } catch (error) {
            console.warn('TableAgGrid: Error in requestAnimationFrame callback', error);
          }
        });
        return;
      }

      if (hasColDiff) {
        // Only column definitions changed
        lastAppliedColumnDefsRef.current = finalColumnDefs;
        api.setGridOption('columnDefs', finalColumnDefs);
        api.refreshToolPanel?.();
        api.refreshCells({ force: true });
        return;
      }

      if (hasGroupByChange) {
        // Only groupBy changed
        updateGrouping(api, groupBy);
        requestNextFrame(() => {
          if (!isGridApiValid(api)) return;
          try {
            applyDefaultExtendsRowsIfNeeded(api);
            api.refreshCells({ force: true });
          } catch (error) {
            console.warn('TableAgGrid: Error updating rows after groupBy change', error);
          }
        });
      }
    } catch (error) {
      console.warn('TableAgGrid: Error updating columns/grouping', error);
    }
  }, [api, finalColumnDefs, groupBy, updateGrouping, applyDefaultExtendsRowsIfNeeded, requestNextFrame]);

  useEffect(() => {
    if (!isGridApiValid(api)) return;
    if (!defaultExtendsRows || Object.keys(defaultExtendsRows).length === 0) return;

    requestNextFrame(() => applyDefaultExtendsRowsIfNeeded(api));
  }, [api, defaultExtendsRows, applyDefaultExtendsRowsIfNeeded, requestNextFrame]);

  useEffect(() => {
    if (!isGridApiValid(api)) return;
    try {
      api.setGridOption('loading', isLoading);
    } catch (error) {
      console.warn('TableAgGrid: Error setting loading state', error);
    }
  }, [api, isLoading]);

  useEffect(() => {
    if (!isGridApiValid(api) || !changeGroupByCallback) return;
    try {
      changeGroupByCallback(api, groupBy);
    } catch (error) {
      console.warn('TableAgGrid: Error in changeGroupByCallback', error);
    }
  }, [api, groupBy, changeGroupByCallback]);

  useEffect(() => {
    return () => {
      cancelNextFrame();
      setApi(null);
    };
  }, [cancelNextFrame]);

  const getRowIdDefault = useCallback(({ data: { id } }) => String(id), []);

  const onColumnStateChanged = useCallback(
    (event) => {
      if (!event?.api || !isGridApiValid(event.api)) return;
      // Skip saves triggered by programmatic column-def updates (e.g. branch change).
      // Only save when the user explicitly drags or uses the tool panel.
      const userSources = ['uiColumnMoved', 'toolPanelUi', 'toolPanelDragAndDrop'];
      if (!userSources.includes(event.source)) return;
      try {
        saveColumnState(event.api);
      } catch (error) {
        console.warn('TableAgGrid: Error saving column state', error);
      }
    },
    [saveColumnState]
  );

  const onRowGroupOpened = useCallback((event) => {
    if (!event?.api || !isGridApiValid(event.api)) return;
    try {
      event.api.refreshCells({ force: true });
    } catch (error) {
      console.warn('TableAgGrid: Error refreshing cells on row group opened', error);
    }
  }, []);

  const onGridReady = useCallback(
    (event) => {
      if (!isGridApiValid(event?.api)) return;
      setApi(event.api);
      lastAppliedDefaultExtendsKeyRef.current = null;
      try {
        loadColumnState(event.api);
        event.api.refreshToolPanel?.();
        lastAppliedColumnDefsRef.current = finalColumnDefs;

        if (defaultSortColumn) {
          event.api.applyColumnState({
            state: [{ colId: defaultSortColumn, sort: 'asc' }],
            defaultState: { sort: null }
          });
        }
      } catch (error) {
        console.warn('TableAgGrid: Error in onGridReady', error);
      }

      const handleFirstDataRendered = () => {
        if (!isGridApiValid(event.api)) return;
        try {
          applyDefaultExtendsRowsIfNeeded(event.api);
          event.api.removeEventListener('firstDataRendered', handleFirstDataRendered);
        } catch (error) {
          console.warn('TableAgGrid: Error in handleFirstDataRendered', error);
        }
      };
      event.api.addEventListener('firstDataRendered', handleFirstDataRendered);
    },
    [loadColumnState, defaultSortColumn, finalColumnDefs, applyDefaultExtendsRowsIfNeeded]
  );

  const onGridPreDestroyed = useCallback(() => {
    cancelNextFrame();
    setApi(null);
  }, [cancelNextFrame]);

  const onRowClicked = useCallback(
    (params) => {
      if (!params?.event || !params?.node) return;

      const event = params.event;
      const target = event?.target;
      const isClickableGroupRowElement = clickableGroupRowClasses.some((className) =>
        target?.closest?.(`.${className}`)
      );

      if (!isClickableGroupRowElement && params.node?.group) {
        try {
          params.node.setExpanded(!params.node.expanded);
        } catch (error) {
          console.warn('TableAgGrid: Error toggling row expansion', error);
        }
      }
    },
    [clickableGroupRowClasses]
  );

  const finalDefaultColDef = useMemo(
    () => ({
      minWidth: 115,
      suppressHeaderMenuButton: true,
      filter: false,
      editable: false,
      lockPinned: true,
      ...defaultColDef
    }),
    [defaultColDef]
  );

  const finalSideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          iconKey: 'columns',
          labelKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressExpandablePivotGroups: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true
          }
        }
      ],
      defaultToolPanel: null,
      ...sideBar
    }),
    [sideBar]
  );

  const finalNoRowsOverlayComponentParams = useMemo(
    () => ({
      text: t('emptystateNothingToDisplayYet'),
      ...noRowsOverlayComponentParams
    }),
    [noRowsOverlayComponentParams, t]
  );

  const finalGridOptions = useMemo(
    () => ({
      rowData,
      loading: isLoading,
      columnDefs: finalColumnDefs,
      sideBar: finalSideBar,
      defaultColDef: finalDefaultColDef,
      autoGroupColumnDef,
      noRowsOverlayComponentParams: finalNoRowsOverlayComponentParams,
      pinnedBottomRowData: rowData?.length > 0 && summaryRow ? [summaryRow] : [],
      getRowId: getRowId || getRowIdDefault,
      onGridReady,
      onGridPreDestroyed,
      onColumnVisible: onColumnStateChanged,
      onColumnMoved: onColumnStateChanged,
      onRowClicked,
      onRowGroupOpened,
      context: {
        ...(gridOptions?.context || {})
      },
      ...GRID_STATIC_OPTIONS,
      ...gridOptions
    }),
    [
      rowData,
      summaryRow,
      finalSideBar,
      finalDefaultColDef,
      finalColumnDefs,
      autoGroupColumnDef,
      gridOptions,
      getRowId,
      onGridReady,
      onGridPreDestroyed,
      onColumnStateChanged,
      onRowClicked,
      onRowGroupOpened,
      getRowIdDefault,
      isLoading,
      finalNoRowsOverlayComponentParams
    ]
  );

  if (isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <TableAgGridWrapper ref={gridWrapperRef} className="ag-theme-alpine">
      {isLoadingExtraData && (
        <LoaderOverlay>
          <Loader />
          <LoaderText>{t('payrollTableWaitingStatusText')}</LoaderText>
        </LoaderOverlay>
      )}
      <AgGrid language={language} gridOptions={finalGridOptions} className="payroll-grid-table" />
    </TableAgGridWrapper>
  );
};
